
import {Component} from 'vue-property-decorator';
import DataInfo from '@/components/reports-v3/components/elements/DataInfo.vue';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import InputText from 'primevue/inputtext';
import BaseFilterable from '@/components/reports-v3/components/filterables/BaseFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {getTableName} from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';
import {roundToFixed} from '@/util/number';
import moment from 'moment';
import {FilterMatchMode} from 'primevue/api';
import {PermissionsGroup} from '@/store/models.def';

@Component({
    components: {
        DataTable,
        Column,
        ColumnGroup,
        Row,
        Dropdown,
        InputText,
        Button,
        DataInfo,
        BaseFilterable,
    },
})

export default class StockClosingStockByLocation extends FilterWidget {

    public get permissionIds(): PermissionsGroup[] {
        return ['sales'];
    }

    public get exportFileName() {
        const formattedDate = moment(this.selectedDateRange[1]).format('DD MMM YY');
        return 'Closing Stock by Location ' + 'As Of ' + formattedDate;
    }

    public get dateFormatted() {
        return moment(this.selectedDateRange[0]).format('DD MMM YYYY')
        !== moment(this.selectedDateRange[1]).format('DD MMM YYYY')
            ? `${moment(this.selectedDateRange[0]).format('DD MMM YYYY')} - ${moment(
                this.selectedDateRange[1],
            ).format('DD MMM YYYY')}`
            : `As of ${moment(this.selectedDateRange[0]).format('DD MMM YYYY')}`;
    }

    public get dateFormatted2() {
        return `As of ${moment(this.selectedDateRange[1]).format('DD MMM YYYY')}`;
    }

    public get expensiveHook() {
        const {selectedDateRange} = this;
        return JSON.stringify([
            selectedDateRange,
        ]);
    }

    public get date() {
        return this.selectedDateRange;
    }

    public get dateRange() {
        return this.selectedDateRange;
    }

    public get currencySymbol() {
        return gdbx.currencySymbol + ' ';
    }

    public stockByLocation: any [] = [];

    public tableItems: any = [];

    public expandedRows: any = [];

    public locationFilter: any = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS},
    };

    public itemFilter: any = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS},
    };

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
    > = ['dateRange'];

    public async closingStockGroups() {

    }

    public expandAll() {
        this.expandedRows = this.stockByLocation;
    }

    public collapseAll() {
        this.expandedRows = [];
    }

    public exportFileName2(x) {
        const formattedDate = moment(this.selectedDateRange[1]).format('DD MMM YYYY');
        return x + ' ' + 'As Of ' + formattedDate;
    }

    public thousandFormatter(x) {
        return Math.ceil(x).toLocaleString('en-US');
    }

    public status(x) {
        if (x < 0) {
            return 'No Available Budget To Order';
        }
        if (x > 0) {
            return 'Available Budget To Order';

        }
        if (x === 0) {
            return 'None';
        }
    }

    public exportCSV2() {
        if (this.$refs.dt2Expand as any) {
            (this.$refs.dt2Expand as any).exportCSV();
        }
    }

    public exportAllItemsCSV() {
        const headers = ['Location', 'Item Code', 'Description', 'Stock Group', 'UTD Qty', 'UTD Cost'];
        const rows: string[][] = [];

        for (const location of this.stockByLocation) {
            for (const item of location.expanded) {
                rows.push([
                    location.location,
                    item.itemCode,
                    item.description,
                    item.stockGroup,
                    String(roundToFixed(item.utdQty, 2)),
                    String(roundToFixed(item.utdCost, 2)),
                ]);
            }
        }

        const csvContent = [
            headers.join(','),
            ...rows.map((row) => row.map((cell) => `"${String(cell || '').replace(/"/g, '""')}"`).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const formattedDate = moment(this.selectedDateRange[1]).format('DD MMM YYYY');
        link.href = URL.createObjectURL(blob);
        link.download = `Closing Stock by Location ${formattedDate}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        const queryHead = await this._ensureTablesLoaded(
            ['closingStockBalance', 'globalStocks'] as any[],
        );
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.5);

        const closingTable = getTableName('closingStockBalance');
        const stockTable = getTableName('globalStocks');

        // Fetch closing stock rows and stock descriptions in parallel
        const [closingRows, stockRows] = await Promise.all([
            queryHead.rawQuery(`
                SELECT "LOCATION", "ITEMCODE", "BATCH", "STOCKGROUP",
                    TRY_CAST("UTDQTY" AS DOUBLE) as utd_qty,
                    TRY_CAST("UTDCOST" AS DOUBLE) as utd_cost,
                    TRY_CAST("POSTDATE" AS BIGINT) as post_date,
                    TRY_CAST("SEQ" AS BIGINT) as seq
                FROM ${closingTable}
                WHERE TRY_CAST("POSTDATE" AS BIGINT) <= ${this.selectedDateRange[1]}
            `),
            queryHead.rawQuery(`
                SELECT "CODE" as item_code, "DESCRIPTION" as item_desc
                FROM ${stockTable}
            `),
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.5, 0.8);

        // Build stock description lookup
        const stockMap = new Map<string, string>(
            stockRows.map((r: any) => [r.item_code, r.item_desc || '']),
        );

        // Group by location -> itemCode -> batch, find latest per batch by postDate/seq
        const byLocation: { [loc: string]: any[] } = {};

        for (const row of closingRows) {
            const loc = row.LOCATION || '';
            if (!byLocation[loc]) {
                byLocation[loc] = [];
            }
            byLocation[loc].push(row);
        }

        const nestedArray: { [key: string]: any[] } = {};

        for (const location of Object.keys(byLocation)) {
            const locationItems = byLocation[location];

            // Group by itemCode within this location
            const byItemCode: { [ic: string]: any[] } = {};
            for (const row of locationItems) {
                const ic = row.ITEMCODE || '';
                if (!byItemCode[ic]) {
                    byItemCode[ic] = [];
                }
                byItemCode[ic].push(row);
            }

            nestedArray[location] = [];

            for (const itemCode of Object.keys(byItemCode)) {
                const itemCodeGroup = byItemCode[itemCode];

                // Group by batch within this itemCode
                const byBatch: { [b: string]: any[] } = {};
                for (const row of itemCodeGroup) {
                    const b = row.BATCH || '';
                    if (!byBatch[b]) {
                        byBatch[b] = [];
                    }
                    byBatch[b].push(row);
                }

                let totalUtdQty = 0;
                let totalUtdCost = 0;
                let latestItem: any = null;

                // For each batch, find the latest record and sum up values
                for (const batch of Object.keys(byBatch)) {
                    const batchGroup = byBatch[batch];

                    // Find latest item for this location + itemCode + batch combination
                    const latestBatchItem = batchGroup.reduce((latest: any, current: any) => {
                        const curDate = Number(current.post_date) || 0;
                        const latDate = Number(latest.post_date) || 0;
                        const curSeq = Number(current.seq) || 0;
                        const latSeq = Number(latest.seq) || 0;
                        if (curDate > latDate) { return current; }
                        if (curDate === latDate && curSeq > latSeq) { return current; }
                        return latest;
                    });

                    totalUtdQty += Number(latestBatchItem.utd_qty) || 0;
                    totalUtdCost += Number(latestBatchItem.utd_cost) || 0;

                    // Keep track of the overall latest item for metadata (stockGroup, etc.)
                    const latBatchDate = Number(latestBatchItem.post_date) || 0;
                    const latBatchSeq = Number(latestBatchItem.seq) || 0;
                    if (!latestItem) {
                        latestItem = latestBatchItem;
                    } else {
                        const latItemDate = Number(latestItem.post_date) || 0;
                        const latItemSeq = Number(latestItem.seq) || 0;
                        if (latBatchDate > latItemDate ||
                            (latBatchDate === latItemDate && latBatchSeq > latItemSeq)) {
                            latestItem = latestBatchItem;
                        }
                    }
                }

                nestedArray[location].push({
                    itemCode,
                    stockGroup: latestItem ? (latestItem.STOCKGROUP || '') : '',
                    utdQty: totalUtdQty,
                    utdCost: totalUtdCost,
                    utdCostNumeral: totalUtdCost,
                    description: stockMap.get(itemCode) || '',
                });
            }
        }

        this.generateLoadingText(0.8, 0.95);

        const newArray: any[] = [];

        for (const item of Object.keys(nestedArray)) {
            newArray.push({
                location: item,
                groupQty: nestedArray[item].reduce((a, b) =>
                    a + b.utdQty, 0,
                ),
                groupAmt: nestedArray[item].reduce((a, b) =>
                    a + b.utdCostNumeral, 0,
                ),
                expanded: nestedArray[item].map((items: { utdQty: any; }) => {
                    return {
                        ...items,
                        utdQty: (items.utdQty),
                    };
                }),
            });
        }

        this.stockByLocation = newArray;

        this.generateLoadingText(1);
        this.saveHistory('stockByLocation');
    }
}
